import React, { useEffect, useMemo, useState } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-icons/font/bootstrap-icons.css";
import { showToast } from "../../../../utils/showToast";
import { usePermissions } from "../../../../utils/permissions";
import "../../../../styles/dashboardTheme.css";
import "../../../../styles/managementTheme.css";
import "../styles/centros.css";
import CentrosSummary from "../components/CentrosSummary";
import CentrosTable from "../components/CentrosTable";
import CentroFormModal from "../components/CentroFormModal";
import CentroDeleteModal from "../components/CentroDeleteModal";

export default function CentrosView({
  centros,
  loading,
  listError,
  alerta,
  dismissAlert,
  loadCentros,
  abrirModalNuevo,
  abrirModalEditar,
  abrirModalEliminar,
  cerrarModal,
  modo,
  formNuevo,
  setFormNuevo,
  formEditar,
  setFormEditar,
  savingNuevo,
  savingEdicion,
  deleting,
  centroSeleccionado,
  handleCrearCentro,
  handleActualizarCentro,
  handleEliminarCentro,
}) {
  const { hasPermission } = usePermissions();
  const canCreateCentro = hasPermission("centros.crear");
  const canUpdateCentro = hasPermission("centros.actualizar");
  const canDeleteCentro = hasPermission("centros.eliminar");
  const [tabNuevo, setTabNuevo] = useState("centro");
  const [tabEditar, setTabEditar] = useState("centro");
  const [activeFilter, setActiveFilter] = useState("total");
  const totalCentros = centros.length;
  const totalActivos = centros.filter((centro) => centro.activo).length;
  const totalInactivos = totalCentros - totalActivos;
  const totalContactables = centros.filter(
    (centro) => centro.telefono || centro.email
  ).length;

  const resumenCentros = [
    { key: "total", label: "Total centros", value: totalCentros, icon: "bi bi-buildings", accent: "total" },
    { key: "activos", label: "Activos", value: totalActivos, icon: "bi bi-check-circle", accent: "success" },
    { key: "inactivos", label: "Inactivos", value: totalInactivos, icon: "bi bi-slash-circle", accent: "muted" },
    { key: "contactables", label: "Contactables", value: totalContactables, icon: "bi bi-telephone", accent: "warning" },
  ];

  const quickFilter = (key) => {
    switch (key) {
      case "activos":
      case "inactivos":
      case "contactables":
      case "total":
        setActiveFilter(key);
        break;
      default:
        setActiveFilter("total");
        break;
    }
  };

  const centrosFiltrados = useMemo(() => {
    switch (activeFilter) {
      case "activos":
        return centros.filter((centro) => centro.activo);
      case "inactivos":
        return centros.filter((centro) => !centro.activo);
      case "contactables":
        return centros.filter((centro) => centro.telefono || centro.email);
      default:
        return centros;
    }
  }, [centros, activeFilter]);

  useEffect(() => {
    if (listError) {
      showToast({
        type: "danger",
        message: listError,
        actions: (dismiss) => (
          <button
            type="button"
            className="btn btn-sm text-white"
            onClick={() => {
              dismiss();
              loadCentros();
            }}
          >
            Reintentar
          </button>
        ),
      });
    }
  }, [listError, loadCentros]);

  useEffect(() => {
    if (alerta?.message) {
      showToast({
        type: alerta.type || "info",
        message: alerta.message,
        onClose: dismissAlert,
      });
    }
  }, [alerta, dismissAlert]);

  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    if (modo) document.body.classList.add("modal-open");
    else document.body.classList.remove("modal-open");
    return () => document.body.classList.remove("modal-open");
  }, [modo]);

  useEffect(() => {
    if (modo === "nuevo") setTabNuevo("centro");
    if (modo === "editar") setTabEditar("centro");
  }, [modo]);

  return (
    <section className="management-page management-theme-teal centros centros-wrapper">
      <div className="management-card management-header mb-4">
        <div>
          <p className="management-eyebrow mb-1">Operativo / Centros</p>
          <h4 className="management-title mb-0 d-flex align-items-center gap-2">
            <i className="bi bi-building" />
            Centros de capacitacion
          </h4>
          <small className="text-muted">
            Gestiona todos los centros y manten actualizada su informacion de contacto.
          </small>
        </div>
        {canCreateCentro && (
          <button className="btn management-btn-primary" onClick={abrirModalNuevo}>
            <i className="bi bi-plus-circle me-2" />
            Nuevo Centro
          </button>
        )}
      </div>

      <CentrosSummary
        resumen={resumenCentros}
        onQuickFilter={quickFilter}
        activeKey={activeFilter}
      />

      <CentrosTable
        centros={centrosFiltrados}
        loading={loading}
        listError={listError}
        loadCentros={loadCentros}
        canCreateCentro={canCreateCentro}
        canUpdateCentro={canUpdateCentro}
        canDeleteCentro={canDeleteCentro}
        abrirModalNuevo={abrirModalNuevo}
        abrirModalEditar={abrirModalEditar}
        abrirModalEliminar={abrirModalEliminar}
      />

      <CentroFormModal
        open={modo === "nuevo"}
        eyebrow="Crear registro"
        title="Nuevo centro"
        icon="bi bi-plus-circle"
        tab={tabNuevo}
        setTab={setTabNuevo}
        form={formNuevo}
        setForm={setFormNuevo}
        saving={savingNuevo}
        onClose={cerrarModal}
        onSubmit={handleCrearCentro}
        submitLabel="Guardar"
      />

      <CentroFormModal
        open={modo === "editar" && centroSeleccionado && canUpdateCentro}
        eyebrow="Actualizar registro"
        title="Editar centro"
        icon="bi bi-pencil-square"
        tab={tabEditar}
        setTab={setTabEditar}
        form={formEditar}
        setForm={setFormEditar}
        saving={savingEdicion}
        onClose={cerrarModal}
        onSubmit={handleActualizarCentro}
        submitLabel="Guardar Cambios"
        submitDisabled={!canUpdateCentro}
      />

      <CentroDeleteModal
        open={modo === "eliminar" && centroSeleccionado && canDeleteCentro}
        centro={centroSeleccionado}
        deleting={deleting}
        onClose={cerrarModal}
        onConfirm={handleEliminarCentro}
      />
    </section>
  );
}
